import { formatCurrency, formatDateTime } from "./format";

// Heuristic formatting for the MVP-E generic dashboard renderer
// (components/ejecutivo/AutoDashboard.tsx). Backend field names follow a
// fairly consistent vocabulary across the contract (spend/revenue/bcm/
// collected/amount/ticket/pipeline -> money; *_pct/*_rate/conversion/
// probability -> percent; *_at/*_date -> date), so we key off substrings in
// the field name rather than requiring a fixed schema per endpoint.

const MONEY_HINTS = [
  "spend", "revenue", "bcm", "collected", "amount", "ticket", "pipeline", "cost", "price", "commission",
  "fondo", "contribucion", "facturacion", "cobrado", "pendiente", "descuento", "devolucion", "value",
  "ltv", "cac",
];
const PERCENT_HINTS = ["pct", "rate", "conversion", "probability", "margen", "share", "mix"];
const DATE_HINTS = ["_at", "_date", "fecha"];
const INTEGER_HINTS = ["count", "leads", "sales", "ventas", "qualified", "appointments", "citas", "puntos", "points", "n_"];

export function humanizeKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function hints(key: string, list: string[]): boolean {
  const k = key.toLowerCase();
  return list.some((h) => k.includes(h));
}

export function formatSmart(key: string, value: any): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (typeof value === "string") {
    if (hints(key, DATE_HINTS) && !isNaN(Date.parse(value))) return formatDateTime(value);
    return value;
  }
  if (typeof value !== "number" || Number.isNaN(value)) return String(value);

  if (hints(key, PERCENT_HINTS)) {
    const pct = Math.abs(value) <= 1.5 ? value * 100 : value;
    return `${pct.toFixed(1)}%`;
  }
  if (hints(key, MONEY_HINTS) && !hints(key, INTEGER_HINTS)) {
    return formatCurrency(value);
  }
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2);
}

export interface MetricLike {
  value: any;
  drilldown?: { endpoint: string; params?: Record<string, any> } | string | null;
  rows?: any[];
  label?: string;
}

export function isMetricLike(v: any): v is MetricLike {
  return v !== null && typeof v === "object" && !Array.isArray(v) && "value" in v && (typeof v.value !== "object" || v.value === null);
}

export function resolveDrilldown(dd: MetricLike["drilldown"]): { endpoint: string; params?: Record<string, any> } | null {
  if (!dd) return null;
  if (typeof dd === "string") return { endpoint: dd };
  return dd;
}
